// React imports
import React, { useEffect, useRef, useState } from 'react';

// Third party imports
import { useDispatch, useSelector } from 'react-redux';
import { useAuth } from 'react-oidc-context';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

// App imports
import {
  IProject,
  IProjectNote,
  ITimesheet,
  ITimesheetHistory,
  TimesheetModel,
  TimesheetStatusMap,
} from 'src/models/Timesheet';
import { GridTable } from './components/GridTable';
import { TableLoader } from './components/TableLoader';
import { Legends } from './components/Legends';
import { TimesheetFilters } from './components/Filters/TimesheetFilters';
import {
  getActivities,
  getAssignedProjects,
} from 'src/redux/features/session/slice';
import {
  actions,
  getTimesheet,
  getTimesheetByUser,
  getTimesheetHistory,
  getTimesheetHistoryData,
  saveTimesheet,
  submitTimesheet,
} from 'src/redux/features/timesheet/slice';
import {
  getDateandTime,
  isFutureDateAndNotInCurrentWeek,
} from 'src/utils/helpers';
import FormModal from 'src/components/FormModal/Index';
import { CanView } from 'src/components/CanView';
import {
  getUserRoles,
  submitAutoFillInTimesheet,
} from 'src/redux/features/users/usersSlice';
import { HistoryModal } from 'src/components/HistoryModal';
import SharedConfirmModal from 'src/components/ConfirmModal';
import { Flex, Typography, Card, Spin, Tooltip } from 'antd';
import AntDButton from 'src/components/Button';
import en from 'src/messages/en.json';
import { setError } from 'src/redux/features/common/commonSlice';

const TimeSheet = ({ teamTime }: { teamTime?: boolean }) => {
  const dispatch = useDispatch();

  const timesheetModelObj = new TimesheetModel();
  const auth = useAuth();
  const manager = auth?.user?.profile?.peoplemanager === 'True';
  const { Title } = Typography;
  // Function to get the start of the week (Sunday)
  const getStartOfWeek = (date) => {
    const currentDay = dayjs(date).day();

    return dayjs(date).subtract(currentDay, 'day').toDate();
  };
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [weekType, setWeekType] = useState<'week' | 'work_week'>('work_week');
  const [weekDate, setWeekDate] = useState(getStartOfWeek(new Date()));
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showNodata, setShowNodata] = useState(false);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const [selectedReportee, setSelectedReportee] = useState('');
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [timesheet, setTimesheet] = useState<ITimesheet>();
  const [history, setHistory] = useState<ITimesheetHistory[]>();
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [reasonForRejection, setReasonForRejection] = useState('');
  const [autoFillToggle, setAutoFillToggle] = useState(false);
  const [autoFill, setAutoFill] = useState(false);
  const [toggleNoteModal, setToggleNoteModal] = useState(false);
  const [noteViewMode, setNoteViewMode] = useState(false);
  const [disableNotes, setDisableNotes] = useState(false);
  const [selectedProject, setSelectedProject] = useState<IProject | null>(null);
  const [projectNotes, setProjectNotes] = useState<IProjectNote[]>([]);
  const [projectNote, setProjectNote] = useState('');
  const [noteData, setNoteData] = useState('');
  const [noteMode, setNoteMode] = useState<'project' | 'timesheet'>('project');
  const activeWeekRequest = useRef<any>(null);
  const { data: response, loading: timeSheetLoading } =
    useSelector(getTimesheet);
  const {
    data: projects,
    loading: projectsLoading,
    status: projectsStatus,
  } = useSelector(getAssignedProjects);
  const { data: activities, loading: activitiesLoading } =
    useSelector(getActivities);
  const { history: historyData, historyLoading: timeSheetHistoryLoading } =
    useSelector(getTimesheetHistoryData);
  const userRoles = useSelector(getUserRoles);

  const onWorkWeekChange = (value) => {
    setWeekType(value);
    const workWeekTimesheet = timesheetModelObj.fromJson(
      { ...response },
      value === 'work_week',
      manager,
    );

    const newData = Object.assign({}, workWeekTimesheet);

    setTimesheet({ ...newData });
  };

  const fetchTimesheetByUser = (date: Date, resource: string) => {
    if (activeWeekRequest.current) {
      activeWeekRequest.current.abort();
    }

    const payload = {
      resource: resource,
      date: dayjs(date || new Date()).format('YYYY-MM-DD'),
    };

    const promise = dispatch(getTimesheetByUser(payload));

    activeWeekRequest.current = promise; // store latest request

    promise.catch((err: any) => {
      if (err.name === 'AbortError') {
        console.log('Previous week-change request canceled');
      }
    });
  };

  const onWeekChange = (date) => {
    setShowNodata(false);
    setWeekDate(date);
    setTimesheet(null);
    fetchTimesheetByUser(date, selectedReportee || auth?.user?.profile?.email);
  };

  const onReporteeChange = (userEmail: string) => {
    setSelectedReportee(userEmail);
    setShowNodata(false);
    setTimesheet(null);
    fetchTimesheetByUser(weekDate, userEmail || auth?.user?.profile?.email);
  };

  const onToggleAutoFill = async () => {
    setAutoFillToggle(true);
    await dispatch(
      submitAutoFillInTimesheet({
        payload: { email: auth?.user?.profile?.email, autoFill: !autoFill },
      }),
    );
    fetchTimesheetByUser(weekDate, auth?.user?.profile?.email);
    setAutoFillToggle(false);
  };

  const onSave = async () => {
    setIsSaving(true);
    const serialisedTimesheet = TimesheetModel.toJson(timesheet);

    try {
      await dispatch(
        saveTimesheet({
          resource: selectedReportee || auth?.user?.profile?.email,
          date: dayjs(weekDate || new Date()).format('YYYY-MM-DD'),
          payload: {
            items: serialisedTimesheet.items,
            note: projectNotes,
          },
        }),
      ).unwrap();

      fetchTimesheetByUser(weekDate, auth?.user?.profile?.email);
    } catch (error) {
      // Handle error if needed
    } finally {
      setIsSaving(false);
    }
  };

  // Function to open the history modal
  const GetHistory = async () => {
    setIsLoadingHistory(true);
    await dispatch(
      getTimesheetHistory({
        resource: selectedReportee || auth?.user?.profile?.email,
        date: dayjs(weekDate || new Date()).format('YYYY-MM-DD'),
      }),
    );
    setIsLoadingHistory(false);
    setShowHistoryModal(true);
  };
  // Function to close the history modal
  const closeHistoryModal = () => {
    setHistory([]);
    setShowHistoryModal(false);
  };
  const confirmSubmit = () => {
    setShowConfirmModal(true);
  };

  const onApproveClick = () => {
    setShowConfirmModal(true);
  };
  const handleProjectNoteChange = (projectId: number, value: string) => {
    setProjectNotes((prev) => {
      const existing = prev.find((p) => p.projectId === projectId);

      if (existing) {
        if (existing.note === value) return prev;

        return prev.map((p) =>
          p.projectId === projectId ? { ...p, note: value } : p,
        );
      }
    });
  };
  const isEnhancementCategory = (project) => {
    const category = project?.category?.toLowerCase() || '';
    const name = project?.projectName?.toLowerCase() || '';

    return (
      category === 'enhancements' ||
      (category === 'product initiatives' && name.includes('enhancement'))
    );
  };

  const isEnhancementProject = (project) =>
    isEnhancementCategory(project) &&
    project?.activityRows?.some((row) =>
      row?.items?.some((it) => it.duration > 0),
    );

  const enhancementProjectMissingNote = (timesheetProjects) => {
    return timesheetProjects.some((project) => {
      const noteEntry = projectNotes.find(
        (n) => n.projectId === project.projectId,
      );

      return isEnhancementProject(project) && !noteEntry?.note?.trim();
    });
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    const serialisedTimesheet = TimesheetModel.toJson(timesheet);

    if (enhancementProjectMissingNote(timesheet?.projects)) {
      setIsSubmitting(false);
      dispatch(setError(en.Timesheet.Error.notesMandatory));

      return;
    }
    try {
      await dispatch(
        submitTimesheet({
          date: dayjs(weekDate || new Date()).format('YYYY-MM-DD'),
          resource: selectedReportee || auth?.user?.profile?.email,
          payload: {
            ...serialisedTimesheet,
            status: 1,
            note: projectNotes,
          },
        }),
      ).unwrap();

      fetchTimesheetByUser(weekDate, auth?.user?.profile?.email);
    } catch (error) {
      // Handle error if needed
    } finally {
      setIsSubmitting(false);
    }
  };

  const onRejectClick = () => {
    setReasonForRejection('');
    setShowRejectModal(true);
  };

  const handleRejectConfirm = async () => {
    if (!reasonForRejection) {
      dispatch(setError(en.Timesheet.Error.reasonForRejection));
      setShowRejectModal(true);

      return;
    }
    setIsRejecting(true);

    const serialisedTimesheet = TimesheetModel.toJson(timesheet);

    await dispatch(
      submitTimesheet({
        date: dayjs(weekDate || new Date()).format('YYYY-MM-DD'),
        resource: auth?.user?.profile?.email,
        payload: {
          status: 3,
          employee: selectedReportee,
          reason: reasonForRejection,
          items: serialisedTimesheet.items,
        },
        message: 'Rejected reason submitted successfully',
      }),
    );
    fetchTimesheetByUser(weekDate, selectedReportee);

    setIsRejecting(false);
  };

  const handleApproveConfirm = async () => {
    setIsApproved(true);

    const serialisedTimesheet = TimesheetModel.toJson(timesheet);

    await dispatch(
      submitTimesheet({
        date: dayjs(weekDate || new Date()).format('YYYY-MM-DD'),
        resource: auth?.user?.profile?.email,
        payload: {
          status: 2,
          employee: selectedReportee,
          items: serialisedTimesheet.items,
        },
        message: 'Timesheet approved successfully',
      }),
    );
    fetchTimesheetByUser(weekDate, selectedReportee);

    setIsApproved(false);
  };

  const onOpenOrCloseNoteModal = (project: IProject) => {
    const existingNote = projectNotes.find(
      (p) => p.projectId === project.projectId,
    );

    setNoteMode('project');
    setSelectedProject(project);
    setProjectNote(existingNote?.note ?? '');
    setToggleNoteModal(true);
  };
  const onOpenTimesheetNoteModal = () => {
    setNoteMode('timesheet');
    setSelectedProject(null);
    setProjectNote(noteData);
    setToggleNoteModal(true);
  };

  useEffect(() => {
    if (response && response.id != null && !timeSheetLoading) {
      const formattedTimesheet = timesheetModelObj.fromJson(
        response,
        weekType === 'work_week',
        manager,
      );

      setAutoFill(formattedTimesheet.autoFill);
      setNoteData(formattedTimesheet.note ?? '');
      setTimesheet(formattedTimesheet);
      const initialNotes =
        formattedTimesheet?.projects?.map((proj) => ({
          projectId: proj.projectId,
          note: proj.note ?? '',
        })) || [];

      setProjectNotes(initialNotes);
      setShowNodata(!response.projects.length);
      setNoteViewMode(
        TimesheetStatusMap[response.status] === 'Submitted' ||
          TimesheetStatusMap[response.status] === 'Approved' ||
          (teamTime && selectedReportee?.length > 0),
      );
      setDisableNotes(isFutureDateAndNotInCurrentWeek(response.startDate));
    } else {
      setTimesheet(null);
      if (!isFirstRender) {
        setShowNodata(true);
      }
    }
  }, [response]);

  useEffect(() => {
    if (
      auth?.user?.profile?.email &&
      activities &&
      !timeSheetLoading &&
      !projectsLoading &&
      !activitiesLoading
    ) {
      fetchTimesheetByUser(weekDate, auth?.user?.profile?.email);
    }
  }, [
    auth?.user?.profile?.email,
    projects,
    activities,
    projectsLoading,
    activitiesLoading,
  ]);

  useEffect(() => {
    setIsFirstRender(false);
  }, [isFirstRender]);

  useEffect(() => {
    return () => {
      dispatch(actions.resetTimesheet());
    };
  }, []);

  useEffect(() => {
    if (historyData && historyData.length && !timeSheetHistoryLoading) {
      setHistory(historyData);
    }
  }, [historyData]);

  return (
    <>
      <Title level={4} style={{ textAlign: 'left' }}>
        {teamTime ? 'Team Time Sheet' : 'Time Sheet'}
      </Title>
      <Card size="small">
        {(isSaving || isSubmitting || isApproved || isRejecting) && (
          <div className="absolute top-0 left-0 w-full h-full bg-white/70 z-10 flex items-center justify-center">
            <Spin size="small" />
          </div>
        )}
        <TimesheetFilters
          weekDate={weekDate}
          onWeekChange={onWeekChange}
          onWorkWeekChange={onWorkWeekChange}
          onReporteeChange={onReporteeChange}
          weekType={weekType}
          teamTime={teamTime}
          autoFillVal={timesheet ? timesheet.autoFill : autoFill}
          onToggleAutoFill={onToggleAutoFill}
          autoFillToggleStatus={autoFillToggle}
          submissionStatus={
            TimesheetStatusMap[response.status] === 'Submitted' ||
            TimesheetStatusMap[response.status] === 'Approved'
          }
        />
        <div style={{ height: `calc(100vh - 14.375rem` }}>
          {!isSubmitting &&
          !isRejecting &&
          !isSaving &&
          !showNodata &&
          autoFillToggle &&
          (projectsLoading || activitiesLoading || timeSheetLoading) ? (
            <TableLoader />
          ) : (
            ''
          )}

          {!showNodata &&
          !autoFillToggle &&
          timesheet &&
          timesheet.projects?.length ? (
            <GridTable
              data={timesheet}
              columns={timesheet.columns}
              startDate={timesheet.startDate}
              endDate={timesheet.endDate}
              disabled={
                timesheet.disabled ||
                teamTime ||
                TimesheetStatusMap[response.status] === 'Approved'
              }
              weekType={weekType}
              isSubmitted={TimesheetStatusMap[response.status] === 'Submitted'}
              isAutoFillEnabled={autoFill}
              onOpenOrCloseNoteModal={onOpenOrCloseNoteModal}
              viewMode={noteViewMode}
              editMode={response?.note ? true : false}
              reporteeSelected={selectedReportee?.length > 0 ? true : false}
              disableNotes={disableNotes}
              teamTime={teamTime}
              isEnhancementProject={isEnhancementProject}
              onOpenTimesheetNoteModal={onOpenTimesheetNoteModal}
              timesheetNotes={
                !teamTime
                  ? noteData?.length > 0
                  : noteData?.length > 0 && selectedReportee?.length > 0
              }
            />
          ) : (
            <div className="flex items-center justify-center h-full w-full">
              <TableLoader />
            </div>
          )}
          {!teamTime ? (
            <SharedConfirmModal
              title={en.Timesheet.Title.submitTimesheet}
              description="Once the timesheet is submitted, it can't be edited."
              show={showConfirmModal}
              onCancel={() => setShowConfirmModal(false)}
              onConfirm={() => {
                setShowConfirmModal(false);
                onSubmit();
              }}
            />
          ) : (
            <>
              <FormModal
                show={showRejectModal}
                title={
                  <Title
                    level={4}
                    style={{
                      textAlign: 'left',
                      margin: 0,
                      padding: '1rem 1rem 0',
                    }}
                  >
                    {en.Timesheet.Title.rejectedReason}
                    <span style={{ color: 'red' }}>*</span>
                  </Title>
                }
                inputValue={reasonForRejection}
                onCancel={() => setShowRejectModal(false)}
                onConfirm={() => {
                  setShowRejectModal(false);
                  handleRejectConfirm();
                }}
                onInputChange={setReasonForRejection}
                placeholder={en.Timesheet.Placeholder.rejectedReason}
              />
              <SharedConfirmModal
                title={en.Timesheet.Title.approveTimesheet}
                description="Once Timesheet is approve, it can't be edited."
                show={showConfirmModal}
                onCancel={() => setShowConfirmModal(false)}
                onConfirm={() => {
                  setShowConfirmModal(false);
                  handleApproveConfirm();
                }}
              />
            </>
          )}
          <FormModal
            id="addNote"
            show={toggleNoteModal}
            title={
              <Title level={5} style={{ margin: 0, padding: '1rem 1rem 0' }}>
                {noteViewMode || noteMode === 'timesheet'
                  ? en.Timesheet.Title.notesForWeek
                  : selectedProject?.note?.trim()?.length > 0
                    ? en.Timesheet.Title.editNotesForWeek
                    : en.Timesheet.Title.addNotesForWeek}
                {noteMode === 'project' && selectedProject?.projectName && (
                  <Tooltip title={selectedProject?.projectName}>
                    <span className="max-w-[300px] whitespace-nowrap overflow-hidden text-ellipsis inline-block align-bottom ml-1.5">
                      ({selectedProject?.projectName})
                    </span>
                  </Tooltip>
                )}
                {noteMode === 'project' &&
                  !teamTime &&
                  isEnhancementProject(selectedProject) && (
                    <span style={{ color: 'red', marginLeft: 4 }}>*</span>
                  )}
              </Title>
            }
            inputValue={projectNote}
            onCancel={() => {
              if (noteMode === 'project') {
                handleProjectNoteChange(
                  selectedProject?.projectId,
                  projectNote,
                );
              }
              setToggleNoteModal(false);
            }}
            onConfirm={() => {
              // intentionally left blank
            }}
            onInputChange={(value) => {
              setProjectNote(value);
            }}
            placeholder={
              isEnhancementCategory(selectedProject)
                ? en.Timesheet.Placeholder.notesForEnhancement
                : en.Timesheet.Placeholder.enterNotesForWeek
            }
            as={'textarea'}
            disableCnfBtn={!projectNote.length}
            formCtrlStyle={{ height: '40vh', marginBottom: '1%' }}
            maxLength={1000}
            disabled={noteMode === 'timesheet' || noteViewMode}
            footerBtnNotNeeded
          />
        </div>
        <Flex
          justify="space-between"
          align="center"
          style={{ paddingTop: '0.5rem', borderTop: '0.063rem solid #eee' }}
        >
          <Flex gap={'middle'}>
            <Legends />
            {!teamTime && (
              <AntDButton
                type="primary"
                disabled={response.id === 0 ? true : false}
                onClick={async () => {
                  await GetHistory();
                }}
                loading={isLoadingHistory}
                statusOnComplete="History"
                htmlType="button"
              />
            )}
          </Flex>
          {!teamTime && timesheet && timesheet.projects?.length ? (
            <Flex gap={'middle'}>
              <AntDButton
                type="primary"
                onClick={onSave}
                disabled={
                  timesheet.disabled ||
                  isSaving ||
                  TimesheetStatusMap[response.status] === 'Approved'
                }
                loading={isSaving}
                statusOnComplete="Save"
                htmlType="button"
                status="Saving..."
                className="save-btn"
              />
              <AntDButton
                type="primary"
                onClick={confirmSubmit}
                disabled={
                  timesheet.disabled ||
                  isSubmitting ||
                  TimesheetStatusMap[response.status] === 'Approved'
                }
                loading={isSubmitting}
                status=" Submitting..."
                statusOnComplete="Submit"
                htmlType="button"
              ></AntDButton>
              <HistoryModal
                show={showHistoryModal}
                onHide={closeHistoryModal}
                history={history}
                isLoading={isLoadingHistory}
              />
            </Flex>
          ) : (
            TimesheetStatusMap[timesheet?.status] === 'Submitted' &&
            selectedReportee && (
              <CanView
                userRole={userRoles.data}
                allowedRoles={['CapexAdManager']}
              >
                <Flex gap={'middle'}>
                  <AntDButton
                    type="primary"
                    style={{ width: '12.5rem' }}
                    onClick={onApproveClick}
                    loading={isApproved}
                    statusOnComplete="Approve"
                    status="approve..."
                    htmlType="button"
                  />
                  <AntDButton
                    type="default"
                    style={{ width: '12.5rem' }}
                    className="reject-btn"
                    onClick={onRejectClick}
                    loading={isRejecting}
                    status="rejecting..."
                    statusOnComplete="Reject"
                    htmlType="button"
                  />
                </Flex>
              </CanView>
            )
          )}
        </Flex>
      </Card>
    </>
  );
};

export default TimeSheet;
